

import { FONT_MONO } from 'styles/tokens/typography';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tabs,
  Tab
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { CheckCircle as CheckCircleIcon, Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../../auth/Auth';
import WhatsAppTemplatesSettings from './WhatsAppTemplatesSettings';
import BrandLogoPanel from './brand/BrandLogoPanel';
import indiatemplate from 'assets/images/Standard India Template.png';

import quoteindiatemplate from 'assets/images/quote indian template.png';
import quoteglobaltemplate from 'assets/images/quote global template.png';

import orderindiatemplate from 'assets/images/order indian template.png';
import orderglobaltemplate from 'assets/images/order global template.png';

import global from 'assets/images/GlobalUSUKEurope Template.png';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

const templateCategories = [
  { id: 'invoice', label: 'Invoice' },
  { id: 'quote', label: 'Quote' },
  { id: 'order', label: 'Order' },
  { id: 'email', label: 'Email' },
  { id: 'travel-order-email', label: 'Travel Order Email' },
  { id: 'whatsapp', label: 'WhatsApp' },
];

// Define templates based on API response structure
const sampleTemplates = [
  // Invoice Templates
  {
    id: 1,
    name: 'Standard India Template',
    preview: indiatemplate,
    category: 'invoice',
    is_default: false
  },
  {
    id: 2,
    name: 'Global-US/UK/Europe Template',
    preview: global,
    category: 'invoice',
    is_default: false
  },
  {
    id: 3,
    name: 'Custom Template',
    preview: '/path/to/preview3.jpg',
    category: 'invoice',
    is_default: false
  },
  // Quote Templates
  {
    id: 4,
    name: 'Standard India Template',
    preview: quoteindiatemplate,
    category: 'quote',
    is_default: false
  },
  {
    id: 5,
    name: 'Global-US/UK/Europe Template',
    preview: quoteglobaltemplate,
    category: 'quote',
    is_default: false
  },
  {
    id: 6,
    name: 'Custom Template',
    preview: '/path/to/preview3.jpg',
    category: 'quote',
    is_default: false
  },
  // Order Templates
  {
    id: 7,
    name: 'Standard India Template',
    preview: orderindiatemplate,
    category: 'order',
    is_default: false
  },
  {
    id: 8,
    name: 'Global-US/UK/Europe Template',
    preview: orderglobaltemplate,
    category: 'order',
    is_default: false
  },
  {
    id: 9,
    name: 'Custom Template',
    preview: '/path/to/preview3.jpg',
    category: 'order',
    is_default: false
  },
  // Email Templates
  // {
  //   id: 10,
  //   name: 'Standard India Template',
  //   preview: '/path/to/preview6.jpg',
  //   category: 'email',
  //   is_default: false
  // },
];

function TemplatesSettings() {
  const { user, axiosInstance } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('invoice');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [defaultTemplateMap, setDefaultTemplateMap] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [newDefaultTemplate, setNewDefaultTemplate] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [imageToShow, setImageToShow] = useState('');
  
  // Email template creation states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    content: '',
    format: 'html'
  });
  const [activeTab, setActiveTab] = useState(0);
  
  // Template preview states
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const initTemplates = async () => {
      try {
        setIsLoading(true);
        // Only initialize templates if they don't exist yet
        // This prevents deleting user-created email templates on every page load
        const existingTemplates = await axiosInstance.get('/templates/');
        if (existingTemplates.data.length === 0) {
          // Only initialize if no templates exist
          await axiosInstance.post('/templates/init');
        }
        await fetchDefaultTemplate();
      } catch (error) {
        console.error("Error initializing templates", error);
        setSnackbarMessage('Failed to initialize templates.');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    initTemplates();
  }, []);

  const fetchDefaultTemplate = async () => {
    try {
      let response;
      
      // Use different endpoint for email templates to get only user-created ones
      if (selectedCategory === 'email') {
        response = await axiosInstance.get(`/templates/email`);
      } else {
        response = await axiosInstance.get(`/templates/`);
      }
      
      console.log("Templates fetched:", response.data);
      setTemplates(response.data);

      // Create defaultTemplateMap from API response
      const map = {};
      templateCategories.forEach(({ id }) => {
        const defaultT = response.data.find(
          (template) => template.type === id && template.is_default
        );
        if (defaultT) {
          const sampleTemplate = sampleTemplates.find(
            t => t.name === defaultT.name && t.category === defaultT.type
          );
          map[id] = {
            id: defaultT.id,
            name: defaultT.name,
            category: defaultT.type,
            is_default: defaultT.is_default,
            preview: sampleTemplate?.preview || null
          };
        }
      });
      setDefaultTemplateMap(map);

      // Set selectedTemplate for the current selectedCategory
      const defaultTemplate = response.data.find(
        (template) => template.type === selectedCategory && template.is_default
      );
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate.id);
      } else {
        const firstTemplate = response.data.find(
          (template) => template.type === selectedCategory
        );
        if (firstTemplate) {
          setSelectedTemplate(firstTemplate.id);
        }
      }
    } catch (error) {
      console.error("Error fetching default template", error);
      setSnackbarMessage('Failed to fetch templates.');
      setSnackbarOpen(true);
    }
  };

  // Update selectedTemplate when selectedCategory changes
  useEffect(() => {
    if (selectedCategory === 'whatsapp') return undefined;
    const updateTemplates = async () => {
      await fetchDefaultTemplate();
    };
    
    updateTemplates();
  }, [selectedCategory]);

  const handleDeleteTemplate = (template) => {
    setTemplateToDelete(template);
    setOpenDeleteDialog(true);
  };
  
  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      setIsDeleting(true);
      await axiosInstance.delete(`/templates/${templateToDelete.id}`);
      setSnackbarMessage(`Template "${templateToDelete.name}" deleted successfully.`);
      setSnackbarOpen(true);
      await fetchDefaultTemplate(); 
    } catch (error) {
      console.error('Error deleting template:', error);
      setSnackbarMessage('Failed to delete template: ' + (error.response?.data?.detail || error.message));
      setSnackbarOpen(true);
    } finally {
      setOpenDeleteDialog(false);
      setTemplateToDelete(null);
      setIsDeleting(false);
    }
  };
  

  const handleTemplateClick = (template) => {
    if (template.id !== selectedTemplate) {
      setNewDefaultTemplate(template);
      setOpenDialog(true);
    }
  };

  const handleConfirmDefault = async () => {
    try {
      setIsSaving(true);
      await updateDefaultTemplate(newDefaultTemplate.id, newDefaultTemplate.name, selectedCategory);
      const updatedMap = { ...defaultTemplateMap };
      updatedMap[selectedCategory] = newDefaultTemplate;
      setDefaultTemplateMap(updatedMap);
      setSelectedTemplate(newDefaultTemplate.id);

      // Update templates state: set is_default to true for the new default, false for others in the same category
      setTemplates((prev) =>
        prev.map((t) =>
          t.type === selectedCategory
            ? { ...t, is_default: t.id === newDefaultTemplate.id }
            : t
        )
      );
      setSnackbarMessage(`"${newDefaultTemplate.name}" is now the default for ${selectedCategory}.`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating default template:', error);
      setSnackbarMessage('Failed to update default template: ' + (error.response?.data?.detail || error.message));
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
      setOpenDialog(false);
    }
  };

  const updateDefaultTemplate = async (templateId, templateName, type) => {
    try {
      console.log('Updating default template:', { id: templateId, name: templateName, type });
      const response = await axiosInstance.put(`/templates/default`, {
        id: templateId,
        name: templateName,
        type: type,
      });
      // Refetch templates to ensure state is in sync with backend
      await fetchDefaultTemplate();
      return response.data;
    } catch (error) {
      console.error('Error updating default template in API:', error);
      throw error;
    }
  };

  const handleImageClick = (image) => {
    setImageToShow(image);
    setOpenImageDialog(true);
  };

  const handleCreateTemplate = () => {
    setNewTemplate({
      name: '',
      content: '',
      format: 'html'
    });
    setActiveTab(0);
    setOpenCreateDialog(true);
  };

  const handleCreateEmailTemplate = async () => {
    try {
      setIsCreating(true);
      
      const templateData = {
        // type: 'email',
        type: templateCategories.find((item) => (item.id === selectedCategory))?.id,
        name: newTemplate.name,
        content: newTemplate.content,
        format: newTemplate.format,
        is_default: false
      };

      await axiosInstance.post('/templates/email', templateData);
      
      setSnackbarMessage('Email template created successfully!');
      setSnackbarOpen(true);
      setOpenCreateDialog(false);
      
      // Refresh templates
      await fetchDefaultTemplate();
    } catch (error) {
      console.error('Error creating email template:', error);
      setSnackbarMessage('Failed to create email template: ' + (error.response?.data?.detail || error.message));
      setSnackbarOpen(true);
    } finally {
      setIsCreating(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setOpenPreviewDialog(true);
  };

  return (
    <MDBox>
      {/* Logo and colours on quotation, order and invoice PDFs, with a live preview. */}
      <BrandLogoPanel
        axiosInstance={axiosInstance}
        notify={(message) => {
          setSnackbarMessage(message);
          setSnackbarOpen(true);
        }}
      />

      <Grid container spacing={2}>

        {/* Sidebar */}

        <Grid item xs={12} md={3}>
          <MDBox
            bgColor="white"
            sx={{
              borderRadius: '8px',
              p: 1.5,
              height: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}
          >
            <List>
              {templateCategories.map((category) => (
                <ListItem
                  key={category.id}
                  button
                  selected={selectedCategory === category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  sx={{
                    borderRadius: '6px',
                    mb: 0.5,
                    py: 0.75,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      color: '#6366F1',
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.12)',
                      }
                    },
                  }}
                >
                  <ListItemText
                    primary={category.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: selectedCategory === category.id ? 600 : 400
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </MDBox>
        </Grid>

        {/* Template List */}
        <Grid item xs={12} md={9}>
          <MDBox
            bgColor="white"
            sx={{
              borderRadius: '8px',
              p: 2,
              height: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}
          >
            {selectedCategory === 'whatsapp' ? (
              <WhatsAppTemplatesSettings />
            ) : (
            <>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium" sx={{ fontSize: '1rem' }}>
                {templateCategories.find((item) => item.id === selectedCategory)?.label} Templates
              </MDTypography>
              {(selectedCategory === 'email' || selectedCategory === 'travel-order-email') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateTemplate}
                  sx={{
                    backgroundColor: 'info.main',
                    color: 'white !important',
                  }}
                >
                  Create Template
                </Button>
              )}
            </MDBox>

            {isLoading ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </MDBox>
            ) : (
              <>
                {templates
                  .filter(template =>
                    template.type === selectedCategory
                  )
                  .sort((a, b) => a.id - b.id)
                  .length === 0 ? (
                  <MDBox 
                    display="flex" 
                    flexDirection="column"
                    justifyContent="center" 
                    alignItems="center" 
                    minHeight="300px"
                    textAlign="center"
                  >
                    {(selectedCategory === 'email' || selectedCategory === 'travel-order-email') ? (
                      <>
                        <MDTypography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                          No Email Templates Yet
                        </MDTypography>
                        <MDTypography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          Create your first email template to get started
                        </MDTypography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleCreateTemplate}
                          sx={{
                            backgroundColor: '#6366F1',
                            color: 'white !important',
                          }}
                        >
                          Create Your First Template
                        </Button>
                      </>
                    ) : (
                      <MDTypography variant="h6" color="text.secondary">
                        No {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Templates Available
                      </MDTypography>
                    )}
                  </MDBox>
                ) : (
                  <Grid container spacing={3}>
                    {templates
                      .filter(template =>
                        template.type === selectedCategory
                      )
                      .sort((a, b) => a.id - b.id)
                      .map((template) => {
                        const sampleTemplate = sampleTemplates.find(
                          t => t.name === template.name && t.category === template.type
                        ) || {};
                        const hasValidImage = sampleTemplate.preview && !sampleTemplate.preview.includes('/path/to/');
                        return (
                          <Grid item xs={12} sm={6} md={4} key={template.id}>
                            <Card
                              onClick={() => handleTemplateClick({
                                id: template.id,
                                name: template.name,
                                category: template.type,
                                is_default: template.is_default,
                                preview: sampleTemplate.preview || null
                              })}
                              sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                position: 'relative',
                                border: selectedTemplate === template.id ? '2px solid #6366F1' : '1px solid #e0e0e0',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                },
                                overflow: 'hidden',
                              }}
                            >
                              {selectedTemplate === template.id && (
                                <CheckCircleIcon
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    color: '#6366F1',
                                    backgroundColor: 'white',
                                    borderRadius: '50%',
                                    fontSize: '1.25rem',
                                    zIndex: 10,
                                  }}
                                />
                              )}
                              {hasValidImage ? (
                                <Box
                                  sx={{
                                    position: 'relative',
                                    width: '100%',
                                    paddingTop: '125%', // 4:5 aspect ratio
                                    overflow: 'hidden',
                                    backgroundColor: '#f8f9fa',
                                    display: 'flex', // Add flex to center content
                                    alignItems: 'center', // Center vertically
                                    justifyContent: 'center', // Center horizontally
                                  }}
                                >
                                  <CardMedia
                                    component="img"
                                    src={sampleTemplate.preview}
                                    alt={template.name}
                                    sx={{
                                      position: 'absolute ',
                                      top: '50% !important', // Center vertically
                                      left: '50% ', // Center horizontally
                                      transform: 'translate(-50%, -50%) ', // Adjust for true centering
                                      width: '100% ',
                                      height: '100% ',
                                      objectFit: 'contain ', // Keep this to maintain aspect ratio
                                      objectPosition: 'center ', // Fallback for older browsers
                                      imageRendering: 'auto ',
                                      zIndex: 1,
                                      margin: '0px !important', // Reset margin
                                    }}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    width: '100%',
                                    paddingTop: '125%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f8f9fa',
                                    position: 'relative',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      textAlign: 'center',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Preview Template
                                  </Typography>
                                </Box>
                              )}
                              <CardContent sx={{
                                p: 1.5, // Reduced padding
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                minHeight: '60px' // Ensures consistent height for name area
                              }}>
                                <Typography
                                  variant="body2"
                                  component="div"
                                  sx={{
                                    fontWeight: 500, // Medium weight instead of light (200)
                                    color: selectedTemplate === template.id ? '#6366F1' : 'text.secondary', // Changed to secondary for subtlety
                                    wordBreak: 'break-word',
                                    maxWidth: '100%',
                                    fontSize: '0.875rem' // Explicit small font size
                                  }}
                                >
                                  {template.name}
                                </Typography>
                                
                                {/* Show format for email templates */}
                                {template.type === 'email' && template.format && (
                                  <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                      fontSize: '0.7rem',
                                      mt: 0.5,
                                      backgroundColor: template.format === 'html' ? '#e3f2fd' : '#f3e5f5',
                                      color: template.format === 'html' ? '#1565C8' : '#7b1fa2',
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: '4px',
                                      fontWeight: 500
                                    }}
                                  >
                                    {template.format.toUpperCase()}
                                  </Typography>
                                )}
                              </CardContent>
                              
                                                            {/* Action buttons - Left side */}
                              <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}>
                                {/* Eye Icon for all templates (including email) */}
                                {((template.type === 'email' && template.content) || template.type === 'travel-order-email') ? (
                                  <Tooltip title="Preview Template">
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent card click
                                        handlePreviewTemplate(template);
                                      }}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                        color: '#6366F1',
                                        '&:hover': {
                                          backgroundColor: 'rgba(255,255,255,1)',
                                        },
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : hasValidImage ? (
                                  <Tooltip title="View Image">
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent card click
                                        handleImageClick(sampleTemplate.preview);
                                      }}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                        color: '#6366F1',
                                        '&:hover': {
                                          backgroundColor: 'rgba(255,255,255,1)',
                                        },
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                                
                                {/* Delete button for email templates only */}
                                {(template.type === 'email' || template.type === 'travel-order-email') && (
                                  <Tooltip title="Delete Template">
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteTemplate(template);
                                      }}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                        color: '#ef4444',
                                        ml: 0.5,
                                        '&:hover': {
                                          backgroundColor: 'rgba(255,255,255,1)',
                                        },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Card>
                          </Grid>
                        );
                      })}
                  </Grid>
                )}
              </>
            )}
            </>
            )}
          </MDBox>
        </Grid>
      </Grid>

      {/* Confirm Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Default Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to make "{newDefaultTemplate?.name}" the default template for "{selectedCategory}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            color="secondary"
            variant="contained"
            disabled={isSaving}
            sx={{ mr: 1, color: 'white !important', borderColor: 'white' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDefault}
            color="primary"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ color: 'white !important' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
  open={openDeleteDialog}
  onClose={() => setOpenDeleteDialog(false)}
>
  <DialogTitle>Delete Template</DialogTitle>
  <DialogContent>
    <DialogContentText>
      Are you sure you want to delete 
      <strong> "{templateToDelete?.name}"</strong>? 
      This action cannot be undone.
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button 
      onClick={() => setOpenDeleteDialog(false)} 
      color="secondary" 
      variant="contained"
      sx={{ color: "white !important" }}
    >
      Cancel
    </Button>
    <Button 
      onClick={confirmDeleteTemplate} 
      color="error" 
      variant="contained"
      sx={{ 
        color: "white !important", 
        backgroundColor: "#ef4444 !important",
        "&:hover": {
          backgroundColor: "#dc2626 !important"
        }
      }}
      disabled={isDeleting}
    >
      {isDeleting ? <CircularProgress size={20} color="inherit" /> : "Delete"}
    </Button>
  </DialogActions>
</Dialog>


      {/* Create Email Template Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Email Template</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} >
            <Grid item xs={12} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Template Name"
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <RadioGroup
                      row
                      value={newTemplate.format}
                      onChange={(e) => setNewTemplate({ ...newTemplate, format: e.target.value })}
                    >
                      <FormControlLabel value="html" control={<Radio />} label="HTML" />
                      <FormControlLabel value="text" control={<Radio />} label="Text" />
                    </RadioGroup>
                  }
                  label="Format"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {newTemplate.format === 'html' 
                  ? 'Write your HTML email template below. You can use HTML tags and variables like {{name}}, {{email}}, etc.'
                  : 'Write your text email template below. You can use variables like {{name}}, {{email}}, etc.'
                }
              </MDTypography>
            </Grid>
            <Grid item xs={12}>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab label="Editor" />
                <Tab label="Preview" />
              </Tabs>
              
              {activeTab === 0 && (
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  label="Email Content"
                  value={newTemplate.content}
                  onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                  required
                  placeholder={newTemplate.format === 'html' ? 
                    '<html><body><h1>Hello {{name}}</h1><p>Write your HTML email content here...</p><p>Use variables like {{name}}, {{email}}, {{company}}</p></body></html>' : 
                    'Hello {{name}},\n\nWrite your text email content here...\n\nYou can use variables like {{name}}, {{email}}, {{company}}\n\nBest regards,\nYour Team'
                  }
                  sx={{
                         '& .MuiInputBase-root': {
                          fontFamily: FONT_MONO,
                      },
                      '& .MuiInputBase-input': {
                             p: 1,   // padding = 1
                            m: 2,   // margin = 2
                          },
                          }}
                                />
                              )}
                              
              {activeTab === 1 && (
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    p: 2,
                    minHeight: '300px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  {newTemplate.format === 'html' ? (
                    <div dangerouslySetInnerHTML={{ __html: newTemplate.content }} />
                  ) : (
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
                      {newTemplate.content}
                    </pre>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenCreateDialog(false)}
            color="secondary"
            variant="contained"
            disabled={isCreating}
            sx={{ mr: 1, color: 'white !important', borderColor: 'white' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateEmailTemplate}
            color="primary"
            variant="contained"
            disabled={isCreating || !newTemplate.name || !newTemplate.content}
            startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ color: 'white !important' }}
          >
            Create Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Template Preview Dialog */}
      <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Preview: {previewTemplate?.name}
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            Format: {previewTemplate?.format?.toUpperCase()}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {previewTemplate?.format === 'html' ? (
              <div 
                dangerouslySetInnerHTML={{ __html: previewTemplate?.content || '' }}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  minHeight: '300px'
                }}
              />
            ) : (
              <pre 
                style={{
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  fontFamily: 'inherit',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '300px',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}
              >
                {previewTemplate?.content || ''}
              </pre>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenPreviewDialog(false)}
            color="primary"
            variant="contained"
            sx={{ color: 'white !important' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setOpenImageDialog(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              color: 'secondary.main',
              backgroundColor: 'white',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          <img src={imageToShow} alt="Template Preview" style={{ width: '100%' }} />
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}

export default TemplatesSettings;
